import { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { MoreHorizontal, CheckCircle2, XCircle, Loader2, UserCheck, Eye, EyeOff } from 'lucide-react';
import { NODE_DEF_BY_KIND, LOOP_MODE_LABELS } from '../../lib/catalog';
import { toRules } from '../../lib/router';
import { cn } from '../../lib/utils';
import { useFlowStore, type StudioNodeData } from '../../store/flowStore';
import type { HumanOversightMode } from '../../types';

const handleClass = '!h-2.5 !w-2.5 !border-2 !border-white !bg-slate';

function NodeImpl({ id, data, selected }: NodeProps) {
  const nodeData = data as StudioNodeData;
  const def = NODE_DEF_BY_KIND[nodeData.kind];
  const Icon = def.icon;

  const runState = useFlowStore((s) => s.runStatus[id]);
  const requestDeleteNodes = useFlowStore((s) => s.requestDeleteNodes);
  const flowOversight = useFlowStore((s) => s.meta?.oversightMode);
  const effectiveMode = (nodeData.oversightOverride ?? flowOversight) as HumanOversightMode | undefined;

  const isLoop = nodeData.kind === 'loop';
  const isReview = nodeData.kind === 'human_review';
  const isRouter = nodeData.kind === 'router';
  const rules = isRouter ? toRules(nodeData.config.conditions) : [];

  // Estado de execução → estilo de borda
  const running = runState?.status === 'running';
  const done = runState?.status === 'done';
  const errored = runState?.status === 'error';
  const awaiting = runState?.status === 'awaiting_human';

  // Destaque por modo de supervisão (seção 6.7)
  const hitlHighlight = isReview && effectiveMode === 'in_the_loop';

  const title = (nodeData.config.label as string) || def.label;

  return (
    <div
      className={cn(
        'group relative rounded-xl border bg-white shadow-soft transition-all',
        isLoop ? 'min-w-[220px]' : 'min-w-[180px]',
        selected ? 'border-brand-orange ring-2 ring-brand-orange/30' : 'border-line',
        running && 'animate-pulse-border border-brand-orange',
        done && 'border-success',
        errored && 'border-danger',
        awaiting && 'border-amber ring-2 ring-amber/40',
        hitlHighlight && !selected && 'ring-2 ring-amber/50',
        isLoop && 'bg-amber/5 border-amber/40',
      )}
    >
      <Handle type="target" position={Position.Left} className={handleClass} />
      <Handle type="target" position={Position.Top} id="t" className={handleClass} />

      <div className="flex items-center justify-between px-3 pt-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate">{def.category}</span>
        <div className="flex items-center gap-1">
          {effectiveMode && <OversightDot mode={effectiveMode} forced={!!nodeData.oversightOverride} />}
          <RunIcon status={runState?.status} />
          <button
            onClick={(e) => {
              e.stopPropagation();
              requestDeleteNodes([id]);
            }}
            className="rounded p-0.5 text-slate opacity-0 transition-opacity hover:bg-cloud group-hover:opacity-100"
            aria-label="Opções"
          >
            <MoreHorizontal className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2.5 px-3 pb-3 pt-1">
        <div
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
          style={{ backgroundColor: def.color }}
        >
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-ink">{title}</p>
          <p className="truncate text-[11px] text-slate">{subtitle(nodeData)}</p>
        </div>
      </div>

      {isLoop && (
        <div className="mx-3 mb-3 rounded-lg border border-dashed border-amber/50 bg-white/60 px-2.5 py-1.5 text-[11px] text-slate">
          <div className="flex items-center justify-between">
            <span className="font-medium text-ink">
              {LOOP_MODE_LABELS[String(nodeData.config.mode)] ?? 'Loop'}
              {nodeData.config.parallel ? ' · paralelo' : ''}
            </span>
            <span className="rounded bg-amber/20 px-1.5 py-0.5 font-semibold text-ink">
              {runState?.iteration ? `${runState.iteration}/` : 'máx '}
              {String(nodeData.config.maxIterations)}
            </span>
          </div>
        </div>
      )}

      {/* Router: uma SAÍDA por regra (cada handle = id da regra) */}
      {isRouter && (
        <div className="mb-2 border-t border-line pt-1.5">
          {rules.length === 0 && (
            <p className="px-3 py-1 text-[11px] italic text-slate">Adicione regras no painel →</p>
          )}
          {rules.map((r) => (
            <div key={r.id} className="relative flex items-center justify-end py-1 pl-3 pr-4">
              <span className="max-w-[150px] truncate text-[11px] font-medium text-ink" title={r.label}>
                {r.label || 'sem nome'}
              </span>
              <Handle
                type="source"
                position={Position.Right}
                id={r.id}
                className={handleClass}
                style={{ position: 'absolute', right: -5, top: '50%' }}
              />
            </div>
          ))}
        </div>
      )}

      {!isRouter && <Handle type="source" position={Position.Right} className={handleClass} />}
      {!isRouter && <Handle type="source" position={Position.Bottom} id="b" className={handleClass} />}
    </div>
  );
}

function RunIcon({ status }: { status?: string }) {
  if (status === 'running') return <Loader2 className="h-3.5 w-3.5 animate-spin text-brand-orange" />;
  if (status === 'done') return <CheckCircle2 className="h-3.5 w-3.5 text-success" />;
  if (status === 'error') return <XCircle className="h-3.5 w-3.5 text-danger" />;
  if (status === 'awaiting_human') return <UserCheck className="h-3.5 w-3.5 text-amber" />;
  return null;
}

function OversightDot({ mode, forced }: { mode: HumanOversightMode; forced: boolean }) {
  const Icon = mode === 'in_the_loop' ? UserCheck : mode === 'on_the_loop' ? Eye : EyeOff;
  const color = mode === 'in_the_loop' ? '#E11019' : mode === 'on_the_loop' ? '#1FAA6B' : '#5B6B7B';
  return (
    <span title={forced ? 'Supervisão sobrescrita neste nó' : 'Supervisão herdada do fluxo'}>
      <Icon className="h-3 w-3" style={{ color, opacity: forced ? 1 : 0.5 }} />
    </span>
  );
}

function subtitle(data: StudioNodeData): string {
  const c = data.config;
  switch (data.kind) {
    case 'trigger':
      return `Gatilho: ${(c.triggerType as string) ?? 'manual'}`;
    case 'agent':
      return c.agentId ? 'Agente vinculado' : 'Selecione um agente';
    case 'llm':
      return (c.model as string) ?? 'LLM';
    case 'tool':
      return `Tool: ${(c.tool as string) ?? '—'}`;
    case 'router':
      return `${toRules(c.conditions).length} regra(s) de saída`;
    case 'memory':
      return `${c.operation === 'write' ? 'Escrever' : 'Ler'} · ${(c.scope as string) ?? 'short'}`;
    case 'kb':
      return `top-${(c.topK as number) ?? 5}`;
    case 'human_review':
      return `SLA ${(c.slaMinutes as number) ?? 30} min`;
    case 'output':
      return `Saída: ${(c.format as string) ?? 'text'}`;
    case 'loop':
      return 'Corpo iterado';
    default:
      return '';
  }
}

export const StudioNode = memo(NodeImpl);
